import { Components, Theme } from '@mui/material';
import type { CSSObject } from '@mui/material/styles';
import type { ToggleButtonProps } from '@mui/material/ToggleButton';
import { getResponsiveFontSize } from '../watchdutyTheme.utils';

/** Matches contained `color="neutral"` (default) and `color="contrast"` (selected) `MuiButton` tokens. */
const getSelectedToggleStyles = (
  theme: Theme,
  color: ToggleButtonProps['color'],
): CSSObject => {
  if (color == null || color === 'standard') {
    return {
      color: 'var(--mui-palette-button-contrast-main)',
      backgroundColor: 'var(--mui-palette-button-contrast-dark) !important',
      borderColor: 'var(--mui-palette-divider) !important',
      '&:hover': {
        backgroundColor: 'var(--mui-palette-button-contrast-light) !important',
        borderColor: 'var(--mui-palette-divider) !important',
      },
    };
  }

  if (color === 'error') {
    return {
      color: 'var(--mui-palette-button-error-main)',
      backgroundColor: 'var(--mui-palette-button-error-dark) !important',
      borderColor: 'var(--mui-palette-button-error-dark) !important',
      '&:hover': {
        backgroundColor: 'var(--mui-palette-button-error-light) !important',
        borderColor: 'var(--mui-palette-button-error-light) !important',
      },
    };
  }

  const paletteColor = theme.palette[color];
  const hoverColor = paletteColor.dark ?? paletteColor.main;

  return {
    color:
      paletteColor.contrastText ??
      theme.palette.getContrastText(paletteColor.main),
    backgroundColor: `${paletteColor.main} !important`,
    borderColor: `${paletteColor.main} !important`,
    '&:hover': {
      backgroundColor: `${hoverColor} !important`,
      borderColor: `${hoverColor} !important`,
    },
  };
};

export const MuiToggleButton: Components['MuiToggleButton'] = {
  styleOverrides: {
    // Use the callback `theme` so palette-dependent selected styles follow the active
    // color scheme (e.g. dark mode primary is brand fill, not light theme's near-black main).
    root: ({ ownerState, theme }) => ({
      fontWeight: (theme as Theme).typography.fontWeightRegular,
      color: 'var(--mui-palette-button-neutral-main)',
      backgroundColor: 'var(--mui-palette-button-neutral-dark) !important',
      border: '1px solid',
      borderColor: 'var(--mui-palette-button-neutral-dark) !important',
      '&:hover': {
        backgroundColor: 'var(--mui-palette-button-neutral-light) !important',
        borderColor: 'var(--mui-palette-button-neutral-light) !important',
      },
      '&.Mui-selected': {
        fontWeight: (theme as Theme).typography.fontWeightBold,
        ...getSelectedToggleStyles(theme as Theme, ownerState.color),
      },
    }),
    sizeLarge: {
      fontSize: getResponsiveFontSize('0.9375rem'),
    },
  },
};
